import {
  Component,
  Injector,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { DvFilterAreaModule } from '@devinco/ui/legacy';
import { LocalStorageItems } from '@shared/constants/local-storage-item';
import { AssetPopupContentComponent } from '@shared/components/asset-popup-content/asset-popup-content.component';
import { DriverPopupContentComponent } from '@shared/components/driver-popup-content/driver-popup-content.component';
import { RegistryComponentNames } from '@shared/constants/registry-component-name';
import { Observable } from 'rxjs';
import { FleetGridComponent } from './components/fleet-grid/fleet-grid.component';
import { FleetMapComponent } from './components/fleet-map/fleet-map.component';
import { AsyncPipe } from '@angular/common';
import { PipesModule } from '@shared/pipes/pipes.module';
import { TranslateModule } from '@ngx-translate/core';
import {
  DvSplitterResizableSide,
  DvVerticalSplitterComponent,
} from '@devinco/ui/dds/splitter';
import { DirectivesModule } from '../../shared/directives/directives.module';
import { EntityFilterService } from '@shared/services/filter-area/entity-filters.service';
import {
  EntityFilter,
  EntityFilterDto,
} from '@shared/interfaces/vt-filter-menu-item';
import { Entity } from '@shared/constants/entity';

@Component({
  imports: [
    DvFilterAreaModule,
    FleetGridComponent,
    FleetMapComponent,
    AsyncPipe,
    PipesModule,
    TranslateModule,
    DirectivesModule,
    DvVerticalSplitterComponent,
  ],
  selector: 'app-fleet-page',
  templateUrl: './fleet-page.component.html',
  styleUrls: ['../../styles/page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [EntityFilterService],
})
export class FleetPageComponent implements OnInit {
  private fleetFiltersService = inject(EntityFilterService);

  resizableSide = DvSplitterResizableSide;
  availableFilters$: Observable<EntityFilter[]>;
  isFleetFilterAreaOpen = true;
  @ViewChild('fleetGrid')
  fleetGrid: FleetGridComponent;

  constructor() {
    const injector = inject(Injector);

    if (!customElements.get(RegistryComponentNames.DriverPopupContent)) {
      const VtDriverMapPopupElement = createCustomElement(
        DriverPopupContentComponent,
        {
          injector,
        }
      );
      customElements.define(
        RegistryComponentNames.DriverPopupContent,
        VtDriverMapPopupElement
      );
    }
    if (!customElements.get(RegistryComponentNames.AssetPopupContent)) {
      const VtAssetMapPopupElement = createCustomElement(
        AssetPopupContentComponent,
        {
          injector,
        }
      );
      customElements.define(
        RegistryComponentNames.AssetPopupContent,
        VtAssetMapPopupElement
      );
    }
  }

  ngOnInit(): void {
    this.getFilters();
    this.toggleFilterAreaVisibility();
  }

  onFilterAreaToggled(isOpen: boolean): void {
    localStorage.setItem(
      LocalStorageItems.FleetFilterAreaToggleKey,
      String(isOpen)
    );
    this.isFleetFilterAreaOpen = isOpen;
  }

  onActiveFilterChanged(dataFilterId: number): void {
    const selectedFilterData =
      this.fleetFiltersService.getSelectedFilterData(dataFilterId);
    if (selectedFilterData) {
      this.fleetFiltersService.setActiveFilter(dataFilterId);
      this.fleetGrid.gridApi?.deselectAll();
      if (selectedFilterData.FilterModel) {
        this.fleetGrid.gridApi?.setFilterModel(selectedFilterData.FilterModel);
      }
    }
  }

  onFleetGridFiltersCleared(): void {
    this.fleetFiltersService.deselectActiveFilters();
  }

  addFilterFn = (): void => {
    const filterModel = this.fleetGrid.gridApi?.getFilterModel() || {};
    this.fleetFiltersService.openAddNewFilterDialog(Entity.FleetFilter, {
      FilterModel: filterModel,
    });
  };

  private getFilters(): void {
    this.fleetFiltersService.updateFilterDataGetterFn = this.updateFilterFn;
    this.fleetFiltersService.fetchFilters(Entity.FleetFilter);
    this.availableFilters$ = this.fleetFiltersService.availableFilters$;
  }

  private toggleFilterAreaVisibility(): void {
    const filterAreaStateValue = localStorage.getItem(
      LocalStorageItems.FleetFilterAreaToggleKey
    );
    this.isFleetFilterAreaOpen =
      filterAreaStateValue === 'true' || filterAreaStateValue === null;
  }

  private updateFilterFn = (): EntityFilterDto | undefined => {
    const activeFilterData = this.fleetFiltersService.getActiveFilter();

    if (!activeFilterData) return;
    return {
      DataFilterID: activeFilterData.DataFilterID,
      Name: activeFilterData.Name,
      FilterModel: this.fleetGrid.gridApi?.getFilterModel() || {},
      VisibleToAllUsers: activeFilterData.VisibleToAllUsers,
    };
  };
}
